import "bootstrap-icons/font/bootstrap-icons.css";
import { Link, Navigate, useNavigate } from "react-router-dom";
import "./sale.css";

import { FilterOutlined, MoreOutlined, SearchOutlined } from "@ant-design/icons";
import {
    Badge,
    Button,
    DatePicker,
    Dropdown,
    Form,
    Menu,
    Pagination,
    Popover,
    Segmented,
    Select,
    Spin,
    Table
} from "antd";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { CSVLink } from "react-csv";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { loadAllSale } from "../../redux/actions/sale/getSaleAction";
import { loadAllStaff } from "../../redux/actions/user/getStaffAction";
import { loadProduct } from "../../redux/actions/product/getAllProductAction";
import DashboardCard from "../Card/DashboardCard";
import SaleReportPrint from "../Invoice/SaleReport";
import SaleInvoice from "../Invoice/SaleInvoice";
import { CACHE_KEYS, getCachedDateFilter, setCachedDateFilter } from "../../utils/dateFilterCache";

/** Sale DB `total_amount` is gross (before line-item % discount). */
function saleGrossTotal(record) {
	return Number(record.total_amount) || 0;
}

/** PDF Grand Total: subtotal after line disc − additional disc + round-off (same as SaleInvoice). */
function saleBillGrandTotal(record) {
	const gross = Number(record.total_amount) || 0;
	const lineDisc = Number(record.total_product_discount) || 0;
	const addDisc =
		record.bill_discount_amount != null && record.bill_discount_amount !== ""
			? Number(record.bill_discount_amount)
			: Number(record.discount) || 0;
	const ro = record.round_off_enabled ? Number(record.round_off_amount) || 0 : 0;
	return gross - lineDisc - addDisc + ro;
}

/** Pending = PDF Grand Total (after line + add. disc + round-off) − paid (matches invoice due logic). */
function salePendingGrandMinusPaid(record) {
	const grand = saleBillGrandTotal(record);
	const paid = Number(record.paid_amount) || 0;
	return grand - paid;
}

function fmtMoney2(n) {
	const x = Number(n);
	if (!Number.isFinite(x)) return "0.00";
	return x.toFixed(2);
}

// //Date fucntionalities
// let startdate = moment(new Date()).format("YYYY-MM-DD");
// let enddate = moment(new Date()).add(1, "day").format("YYYY-MM-DD");

function CustomTable({ list, total, startdate, enddate, user, paginationConfig, onColumnVisibilityReady, onShowPdf, onPrintPdf }) {
  const [columnItems, setColumnItems] = useState([]);
  const [columnsToShow, setColumnsToShow] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { current: currentPage, pageSize: pageSizeProp, totalCount, viewMode, onChange: onPaginationChange } = paginationConfig || {};
  const isAllMode = viewMode === "all";
  const effectivePageSize = isAllMode && totalCount != null ? totalCount : (pageSizeProp || 10);

  // Navigate to sale creation page with pre-filled data (single)
  const handleConvertToSale = (id, type) => {
    navigate(`/sale?sourceType=${type}&sourceId=${id}`);
  };

  // Duplicate any sale-list entry into Create New Sale Invoice (does not change original)
  const handleDuplicateEntry = (id, type = "sale") => {
    const docType = type || "sale";
    navigate(`/sale?sourceType=${docType}&sourceId=${id}`);
  };

  // Multi-select → Create New Sale Invoice (like Purchase List "Convert to Sale")
  const handleCreateSaleFromSelected = () => {
    if (!selectedRows.length) return;
    const shouldProceed = window.confirm(
      `Create a new sale invoice from ${selectedRows.length} selected entr${
        selectedRows.length > 1 ? "ies" : "y"
      }? Line items will be pre-filled so you can review and edit before saving.`
    );
    if (!shouldProceed) return;

    const ids = selectedRows.map((row) => row.id).join(",");
    const docTypes = selectedRows.map((row) => row.document_type || "sale");
    const uniqueTypes = [...new Set(docTypes)];

    if (uniqueTypes.length === 1) {
      navigate(`/sale?sourceType=${uniqueTypes[0]}&sourceIds=${ids}`);
    } else {
      navigate(
        `/sale?sourceType=saleList&sourceIds=${ids}&docTypes=${docTypes.join(",")}`
      );
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys, rows) => {
      setSelectedRowKeys(keys);
      setSelectedRows(rows);
    },
    preserveSelectedRowKeys: true,
  };

  const columns = [
    // {
    //   title: "Id",
    //   dataIndex: "id",
    //   key: "id",
    //   render: (name, { id, document_type }) => {
    //     const docType = document_type || 'sale';
    //     let linkUrl = `/sale/${id}`;
    //     if (docType === 'sales_order') linkUrl = `/sales-order/${id}`;
    //     else if (docType !== 'sale') linkUrl = `/sale/${id}?type=${docType}`;
    //     return <Link to={linkUrl}>{id}</Link>;
    //   },
    // },
    {
      title: "Type",
      key: "document_type",
      render: (name, record) => {
        const docType = record.document_type || 'sale';
        const getBadgeClass = (type) => {
          switch(type) {
            case 'quotation': return 'bg-info';
            case 'challan': return 'bg-warning';
            case 'sales_order': return 'bg-success';
            default: return 'bg-primary';
          }
        };
        const getTypeName = (type) => {
          switch(type) {
            case 'quotation': return 'Quotation';
            case 'challan': return 'Challan';
            case 'sales_order': return 'Sales Order';
            default: return 'Sale';
          }
        };
        return <span className={`badge ${getBadgeClass(docType)}`}>{getTypeName(docType)}</span>;
      },
    },
    {
      title: "Invoice No",
      dataIndex: "invoice_number",
      key: "invoice_number",
      // render: (name, { prefix,invoice_number }) => <Link to={`/sale/${invoice_number}`}>{prefix+invoice_number}</Link>,
      render: (name, { id, prefix, invoice_number, document_type }) => {
        const docType = document_type || 'sale';
        let linkUrl = `/sale/${id}`;
        if (docType === 'sales_order') linkUrl = `/sales-order/${id}`;
        else if (docType !== 'sale') linkUrl = `/sale/${id}?type=${docType}`;
        return <Link to={linkUrl}>{prefix + invoice_number}</Link>;
      },
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (date) => moment(date).format("ll"),
    },
    {
      title: "Customer ",
      dataIndex: `customer`,
      key: "customer_id",
      render: (customer) => customer?.name,
    },

    {
      title: "Total Amt.",
      dataIndex: "total_amount",
      key: "total_amount",
      render: (_, record) => fmtMoney2(saleGrossTotal(record)),
    },
    {
      title: "Discount",
      dataIndex: "total_product_discount",
      key: "total_product_discount",
      render: (value) => fmtMoney2(value ?? 0),
    },
    {
      title: "Add. Discount",
      dataIndex: "bill_discount_amount",
      key: "bill_discount_amount",
      render: (_, record) =>
        fmtMoney2(
          record.bill_discount_amount != null && record.bill_discount_amount !== ""
            ? record.bill_discount_amount
            : Number(record.discount) || 0
        ),
    },
    {
      title: "Bill Amt.",
      key: "bill_amount_rounded",
      render: (_, record) => `₹${Math.round(saleBillGrandTotal(record))}`,
    },
    {
      title: "Paid ",
      dataIndex: "paid_amount",
      key: "paid_amount",
      responsive: ["md"],
      render: (value) => fmtMoney2(value ?? 0),
    },
    {
      title: "Pending Amt.",
      key: "pending_grand_minus_paid",
      responsive: ["md"],
      render: (_, record) => fmtMoney2(salePendingGrandMinusPaid(record)),
    },

    {
      title: "Profit",
      dataIndex: "profit",
      key: "profit",
      responsive: ["md"],
      render: (value) => {
        if (value === undefined || value === null) {
          return "0.00";
        }
        return Number(value).toFixed(2);
      },
    },
    
    {
      title: "Sale",
      dataIndex: "user",
      key: "user",
      render: (user) => user?.username,
      responsive: ["md"],
    },
    {
      title: "Actions",
      key: "actions",
      render: (name, record) => {
        const docType = record.document_type || 'sale';
        const id = record.id;
        
        // Menu items for Quotation/Challan
        if (docType === 'quotation' || docType === 'challan') {
          const menu = (
            <Menu>
              <Menu.Item 
                key="convert"
                onClick={() => {
                  if (window.confirm(`Convert this ${docType} to sale?`)) {
                    handleConvertToSale(id, docType);
                  }
                }}
              >
                Convert to Sale
              </Menu.Item>
              <Menu.Item
                key="duplicate"
                onClick={() => handleDuplicateEntry(id, docType)}
              >
                Duplicate Entry
              </Menu.Item>
              {onShowPdf && (
                <Menu.Item key="showPdf" onClick={() => onShowPdf(record)}>
                  Preview
                </Menu.Item>
              )}
              {onPrintPdf && (
                <Menu.Item key="printPdf" onClick={() => onPrintPdf(record)}>
                  Print PDF
                </Menu.Item>
              )}
              <Menu.Item 
                key="edit"
                onClick={() => {
                  let editUrl = `/sale/edit/${id}`;
                  if (docType === 'sales_order') editUrl = `/sales-order/edit/${id}`;
                  else if (docType !== 'sale') editUrl = `/sale/edit/${id}?type=${docType}`;
                  window.location.href = editUrl;
                }}
              >
                Edit
              </Menu.Item>
            </Menu>
          );
          
          return (
            <Dropdown 
              overlay={menu}
              trigger={['click']}
              placement="bottomRight"
            >
              <Button 
                type="text" 
                onClick={(e) => e.preventDefault()}
              >
                <i className="bi bi-three-dots-vertical" style={{ fontSize: '18px' }}></i>
              </Button>
            </Dropdown>
          );
        }
        
        // Menu items for Sale (all options)
        const menu = (
          <Menu>
            <Menu.Item
              key="duplicate"
              onClick={() => handleDuplicateEntry(id, docType)}
            >
              Duplicate Entry
            </Menu.Item>
            <Menu.Item 
              key="payment"
              onClick={() => {
                window.location.href = `/payment/customer/${id}`;
              }}
            >
              Payment
            </Menu.Item>
            {onShowPdf && (
              <Menu.Item key="showPdf" onClick={() => onShowPdf(record)}>
                Preview
              </Menu.Item>
            )}
            {onPrintPdf && (
              <Menu.Item key="printPdf" onClick={() => onPrintPdf(record)}>
                Print PDF
              </Menu.Item>
            )}
            <Menu.Item 
              key="edit"
              onClick={() => {
                const editUrl = docType === 'sale' ? `/sale/edit/${id}` : `/sale/edit/${id}?type=${docType}`;
                window.location.href = editUrl;
              }}
            >
              Edit
            </Menu.Item>
          </Menu>
        );
        
        return (
          <Dropdown 
            overlay={menu}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button 
              type="text" 
              onClick={(e) => e.preventDefault()}
            >
              <i className="bi bi-three-dots-vertical" style={{ fontSize: '18px' }}></i>
            </Button>
          </Dropdown>
        );
      },
    },
    
  ];

 

  useEffect(() => {
    setColumnItems(menuItems);
    setColumnsToShow(columns);
  }, []);

  const colVisibilityClickHandler = (col) => {
    const ifColFound = columnsToShow.find((item) => item.key === col.key);
    if (ifColFound) {
      const filteredColumnsToShow = columnsToShow.filter(
        (item) => item.key !== col.key
      );
      setColumnsToShow(filteredColumnsToShow);
    } else {
      const foundIndex = columns.findIndex((item) => item.key === col.key);
      const foundCol = columns.find((item) => item.key === col.key);
      let updatedColumnsToShow = [...columnsToShow];
      updatedColumnsToShow.splice(foundIndex, 0, foundCol);
      setColumnsToShow(updatedColumnsToShow);
    }
  };

  useEffect(() => {
    if (onColumnVisibilityReady && list && columnItems.length > 0) {
      onColumnVisibilityReady(
        <Dropdown
          overlay={<Menu onClick={colVisibilityClickHandler} items={columnItems} />}
          placement="bottomLeft"
        >
          <Button className="column-visibility">Column Visibility</Button>
        </Dropdown>
      );
    }
  }, [onColumnVisibilityReady, list, columnItems, columnsToShow]);

  const menuItems = columns.map((item) => {
    return {
      key: item.key,
      label: <span>{item.title}</span>,
    };
  });

  const addKeys = (arr) => arr.map((i) => ({ ...i, key: i.id }));

  return (
    <>
      {list && (
        <div
          className="text-center d-flex flex-wrap justify-content-between align-items-center gap-2 list-page-header mb-2"
          style={{ marginBottom: 8 }}
        >
          <div className="d-flex flex-wrap align-items-center gap-2">
            <Button
              type="primary"
              onClick={handleCreateSaleFromSelected}
              disabled={!selectedRowKeys.length}
            >
              Create Sale Invoice
              {selectedRowKeys.length ? ` (${selectedRowKeys.length})` : ""}
            </Button>
          </div>
        </div>
      )}
      {/* Desktop Table - viewport-filling height so more rows visible and same look on all screen sizes */}
      <div className="d-none d-lg-block list-page-table-container sale-list-table-container" style={{ minHeight: 560 }}>
        <div className="list-page-table-inner">
          <div className="list-page-table-wrap sale-list-table-wrap">
            <Table
              scroll={{ x: true, y: 560 }}
              loading={list === null}
              pagination={{
                current: currentPage || 1,
                pageSize: effectivePageSize || 10,
                total: total ?? (list && list.length) ?? 0,
                pageSizeOptions: [10, 20, 50, 100, 200],
                showSizeChanger: true,
                hideOnSinglePage: false,
                showTotal: (tot, range) => {
                  const t = total ?? (list && list.length) ?? 0;
                  if (!t) return "0 Items";
                  return `${range[0]}-${range[1]} of ${t} Items`;
                },
                onChange: (page, size) => onPaginationChange && onPaginationChange(page, size),
              }}
              rowSelection={rowSelection}
              columns={columnsToShow}
              dataSource={list ? addKeys(list) : []}
            />
          </div>
        </div>
      </div>

      {/* Mobile Card Layout - scrollable so small screens can see all invoices */}
      <div className="d-lg-none sale-list-mobile-cards">
        {list && list.length > 0 ? (
          <div className="row">
            {(isAllMode ? list : list.slice(((currentPage || 1) - 1) * effectivePageSize, (currentPage || 1) * effectivePageSize)).map((record) => (
              <div key={record.id} className="col-12 mb-3">
                <div className="card">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start ">
                      <h6 className="card-title mb-0">
                        <Link 
                          to={record.document_type === 'sale' || !record.document_type 
                            ? `/sale/${record.id}` 
                            : `/sale/${record.id}?type=${record.document_type}`} 
                          style={{ textDecoration: 'none', color: '#1890ff' }}
                        >
                          Invoice #{record.prefix}{record.invoice_number}
                        </Link>
                      </h6>
                      <div>
                        <small className="text-muted">
                          {moment(record.date).format("MMM DD, YYYY")}
                        </small>
                        <br />
                        <span className={`badge ${record.document_type === 'quotation' ? 'bg-info' : record.document_type === 'challan' ? 'bg-warning' : record.document_type === 'sales_order' ? 'bg-success' : 'bg-primary'}`}>
                          {record.document_type === 'quotation' ? 'Quotation' : record.document_type === 'challan' ? 'Challan' : record.document_type === 'sales_order' ? 'Sales Order' : 'Sale'}
                        </span>
                      </div>
                    </div>
                    
                    <div className="mb-2">
                      <small className="text-muted">
                        <strong>Customer:</strong> {record.customer?.name || 'N/A'}
                      </small>
                    </div>
                    
                    <div className="row mb-2">
                      <div className="col-6">
                        <small className="text-muted">
                          <strong>Total:</strong> ₹{fmtMoney2(saleGrossTotal(record))}
                        </small>
                      </div>
                      <div className="col-6">
                        <small className="text-muted">
                          <strong>Discount:</strong> ₹{fmtMoney2(record.total_product_discount ?? 0)}
                        </small>
                      </div>
                    </div>
                    <div className="row mb-2">
                      <div className="col-6">
                        <small className="text-muted">
                          <strong>Add. disc.:</strong> ₹{fmtMoney2(
                            record.bill_discount_amount != null && record.bill_discount_amount !== ""
                              ? record.bill_discount_amount
                              : Number(record.discount) || 0
                          )}
                        </small>
                      </div>
                      <div className="col-6">
                        <small className="text-muted">
                          <strong>Bill (rounded):</strong> ₹{Math.round(saleBillGrandTotal(record))}
                        </small>
                      </div>
                    </div>
                    <div className="row mb-2">
                      <div className="col-6">
                        <small className="text-muted">
                          <strong>Paid:</strong> ₹{fmtMoney2(record.paid_amount ?? 0)}
                        </small>
                      </div>
                      <div className="col-6">
                        <small className="text-muted">
                          <strong>Pending:</strong> ₹{fmtMoney2(salePendingGrandMinusPaid(record))}
                        </small>
                      </div>
                    </div>
                    
                    {record.user && (
                      <div className="mb-2">
                        <small className="text-muted">
                          <strong>Sales Person:</strong> {record.user.username}
                        </small>
                      </div>
                    )}
                    
                    {record.profit && (
                      <div className="mb-2">
                        <small className="text-success">
                          <strong>Profit:</strong> ₹{record.profit}
                        </small>
                      </div>
                    )}
                    
                    {/* Action Buttons */}
                    <div className="d-flex gap-2 mt-2 flex-wrap align-items-center">
                      <Link to={record.document_type === 'sale' || !record.document_type 
                        ? `/sale/${record.id}` 
                        : `/sale/${record.id}?type=${record.document_type}`}>
                        <Button 
                          type="primary" 
                          size="small"
                        >
                          View Details
                        </Button>
                      </Link>
                      {(() => {
                        const docType = record.document_type || 'sale';
                        
                        // Menu items for Quotation/Challan
                        if (docType === 'quotation' || docType === 'challan') {
                          const menu = (
                            <Menu>
                              <Menu.Item 
                                key="convert"
                                onClick={() => {
                                  if (window.confirm(`Convert this ${docType} to sale?`)) {
                                    handleConvertToSale(record.id, docType);
                                  }
                                }}
                              >
                                Convert to Sale
                              </Menu.Item>
                              <Menu.Item
                                key="duplicate"
                                onClick={() => handleDuplicateEntry(record.id, docType)}
                              >
                                Duplicate Entry
                              </Menu.Item>
                              {onShowPdf && (
                                <Menu.Item key="showPdf" onClick={() => onShowPdf(record)}>
                                  Show PDF
                                </Menu.Item>
                              )}
                              {onPrintPdf && (
                                <Menu.Item key="printPdf" onClick={() => onPrintPdf(record)}>
                                  Print PDF
                                </Menu.Item>
                              )}
            <Menu.Item 
              key="edit"
              onClick={() => {
                const editUrl = docType === 'sale' ? `/sale/edit/${record.id}` : `/sale/edit/${record.id}?type=${docType}`;
                window.location.href = editUrl;
              }}
            >
              Edit
            </Menu.Item>
                            </Menu>
                          );
                          
                          return (
                            <Dropdown 
                              overlay={menu}
                              trigger={['click']}
                              placement="bottomRight"
                            >
                              <Button 
                                type="text" 
                                size="small"
                                onClick={(e) => e.preventDefault()}
                              >
                                <i className="bi bi-three-dots-vertical" style={{ fontSize: '18px' }}></i>
                              </Button>
                            </Dropdown>
                          );
                        }
                        
                        // Menu items for Sale (all options)
                        const menu = (
                          <Menu>
                            <Menu.Item
                              key="duplicate"
                              onClick={() => handleDuplicateEntry(record.id, docType)}
                            >
                              Duplicate Entry
                            </Menu.Item>
                            <Menu.Item 
                              key="payment"
                              onClick={() => {
                                window.location.href = `/payment/customer/${record.id}`;
                              }}
                            >
                              Payment
                            </Menu.Item>
                            {onShowPdf && (
                              <Menu.Item key="showPdf" onClick={() => onShowPdf(record)}>
                                Show PDF
                              </Menu.Item>
                            )}
                            {onPrintPdf && (
                              <Menu.Item key="printPdf" onClick={() => onPrintPdf(record)}>
                                Print PDF
                              </Menu.Item>
                            )}
            <Menu.Item 
              key="edit"
              onClick={() => {
                let editUrl = `/sale/edit/${record.id}`;
                if (docType === 'sales_order') editUrl = `/sales-order/edit/${record.id}`;
                else if (docType !== 'sale') editUrl = `/sale/edit/${record.id}?type=${docType}`;
                window.location.href = editUrl;
              }}
            >
              Edit
            </Menu.Item>
                          </Menu>
                        );
                        
                        return (
                          <Dropdown 
                            overlay={menu}
                            trigger={['click']}
                            placement="bottomRight"
                          >
                            <Button 
                              type="text" 
                              size="small"
                              onClick={(e) => e.preventDefault()}
                            >
                              <i className="bi bi-three-dots-vertical" style={{ fontSize: '18px' }}></i>
                            </Button>
                          </Dropdown>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-5">
            <p className="text-muted">No sales found</p>
          </div>
        )}
        
        {/* Mobile Pagination */}
        {list && list.length > 0 && !isAllMode && (
          <div className="d-flex justify-content-center mt-3 mb-4">
            <Pagination
              current={currentPage || 1}
              pageSize={effectivePageSize || 10}
              total={total ?? (list && list.length) ?? 0}
              showSizeChanger
              pageSizeOptions={[10, 20, 50, 100, 200]}
              onChange={(page, size) => onPaginationChange && onPaginationChange(page, size)}
              responsive={true}
              size="small"
            />
          </div>
        )}
      </div>
    </>
  );
}

const GetAllSale = (props) => {
  const dispatch = useDispatch();
  const list = useSelector((state) => state.sales.list);
  const total = useSelector((state) => state.sales.total);
  const userList = useSelector((state) => state.users.list);
  const [user, setUser] = useState("");
  const [viewMode, setViewMode] = useState("paginated"); // "all" | "paginated"
  const [loading, setLoading] = useState(false);
  const [discount, setDiscount] = useState(0);
	const [documentTypeFilter, setDocumentTypeFilter] = useState("all"); // "all", "sale", "quotation", "challan"
	const [filterVisible, setFilterVisible] = useState(false);
	const [columnVisibilityToolbar, setColumnVisibilityToolbar] = useState(null);

	// PDF modal from kebab: Show PDF / Print PDF
	const [pdfModalOpen, setPdfModalOpen] = useState(false);
	const [pdfModalData, setPdfModalData] = useState(null);
	const [pdfModalAction, setPdfModalAction] = useState("preview"); // "preview" | "print"
	const [pdfModalLoading, setPdfModalLoading] = useState(false);

	// Fetch single invoice for PDF (same normalization as detailSaleAction)
	const fetchInvoiceForPdf = async (id, docType) => {
		let endpoint;
		if (docType === "quotation") endpoint = `quotation/${id}`;
		else if (docType === "challan") endpoint = `challan/${id}`;
		else endpoint = `sale-invoice/${id}`;
		const response = await axios.get(endpoint);
		const data = response.data;
		if (docType === "quotation") {
			const quotation = data.data || data;
			if (quotation.quotationInvoiceProduct) {
				quotation.saleInvoiceProduct = quotation.quotationInvoiceProduct;
				delete quotation.quotationInvoiceProduct;
			}
			quotation.paid_amount = quotation.paid_amount || 0;
			quotation.due_amount = quotation.due_amount || 0;
			quotation.profit = quotation.profit || 0;
			quotation.document_type = "quotation";
			return quotation;
		}
		if (docType === "challan") {
			const challan = data.data || data;
			if (challan.challanInvoiceProduct) {
				challan.saleInvoiceProduct = challan.challanInvoiceProduct;
				delete challan.challanInvoiceProduct;
			}
			challan.paid_amount = challan.paid_amount || 0;
			challan.due_amount = challan.due_amount || 0;
			challan.profit = challan.profit || 0;
			challan.document_type = "challan";
			return challan;
		}
		const inv = data.singleSaleInvoice || data.data?.singleSaleInvoice || data;
		if (inv && !inv.document_type) inv.document_type = "sale";
		return inv;
	};

	const handleShowPdf = async (record) => {
		const docType = record.document_type || "sale";
		setPdfModalLoading(true);
		try {
			const invoiceData = await fetchInvoiceForPdf(record.id, docType);
			setPdfModalData(invoiceData);
			setPdfModalAction("preview");
			setPdfModalOpen(true);
		} catch (err) {
			console.error("Failed to load invoice for PDF:", err);
		} finally {
			setPdfModalLoading(false);
		}
	};

	const handlePrintPdf = async (record) => {
		const docType = record.document_type || "sale";
		setPdfModalLoading(true);
		try {
			const invoiceData = await fetchInvoiceForPdf(record.id, docType);
			setPdfModalData(invoiceData);
			setPdfModalAction("print");
			setPdfModalOpen(true);
		} catch (err) {
			console.error("Failed to load invoice for PDF:", err);
		} finally {
			setPdfModalLoading(false);
		}
	};

	// Default date range: from cache or current month
	const [startdate, setStartdate] = useState(() => {
		const c = getCachedDateFilter(CACHE_KEYS.SALE_LIST);
		return c ? c.startdate : moment().startOf("month").format("YYYY-MM-DD");
	});
	const [enddate, setEnddate] = useState(() => {
		const c = getCachedDateFilter(CACHE_KEYS.SALE_LIST);
		return c ? c.enddate : moment().endOf("month").format("YYYY-MM-DD");
	});

	useEffect(() => {
		dispatch(loadAllStaff({ status: true }));
	}, [dispatch]);

  useEffect(() => {
    dispatch(loadProduct({ page: 1, limit: 1000 }));
  }, [dispatch]);
  


  const { RangePicker } = DatePicker;
  const totalCount = total?._count?.id;


	// On mount: load data
	useEffect(() => {
		dispatch(
			loadAllSale({
				page: 1,
				limit: 1000,
				startdate: startdate,
				enddate: enddate,
				user: user || "",
			})
		);
	}, [dispatch]);


	const onSearchFinish = async (values) => {
    const docType = values?.documentType ?? documentTypeFilter;
    setUser(values?.user ?? user);
    setDocumentTypeFilter(docType);
    // Use date range from form if present, else keep current state
    let start = startdate;
    let end = enddate;
    if (values?.dateRange && values.dateRange[0] && values.dateRange[1]) {
      const d0 = values.dateRange[0];
      const d1 = values.dateRange[1];
      start = (d0.format ? d0 : moment(d0)).format("YYYY-MM-DD");
      end = (d1.format ? d1 : moment(d1)).format("YYYY-MM-DD");
      setStartdate(start);
      setEnddate(end);
      setCachedDateFilter(CACHE_KEYS.SALE_LIST, { startdate: start, enddate: end });
    }
    const resp = await dispatch(
      loadAllSale({
        page: 1,
        limit: "",
        startdate: start,
        enddate: end,
        user: values?.user ?? user ?? "",
      })
    );
    if (resp.message === "success") {
      setLoading(false);
    } else {
      setLoading(false);
    }
  };
  const [form] = Form.useForm();

	// When filter popover opens, sync form with current filter state (so date range shows correctly)
	useEffect(() => {
		if (filterVisible) {
			form.setFieldsValue({
				dateRange: [moment(startdate), moment(enddate)],
				documentType: documentTypeFilter,
				user: user || undefined,
			});
		}
	}, [filterVisible, startdate, enddate, documentTypeFilter, user, form]);

  const filteredList = useMemo(() => {
    if (!list) return null;
    if (documentTypeFilter === "all") return list;
    return list.filter((item) => (item.document_type || "sale") === documentTypeFilter);
  }, [list, documentTypeFilter]);

  const filteredTotal = documentTypeFilter === "all" ? (total?._count?.id ?? 0) : (filteredList?.length ?? 0);

  const listForExport = filteredList ?? list;
  const CSVlist = listForExport?.map((i) => ({
    ...i,
    customer: i?.customer?.name,
    total_amount_gross: saleGrossTotal(i),
    line_discount: Number(i.total_product_discount) || 0,
    additional_discount:
      i.bill_discount_amount != null ? Number(i.bill_discount_amount) : Number(i.discount) || 0,
    bill_amount_rounded: Math.round(saleBillGrandTotal(i)),
    grand_total: saleBillGrandTotal(i),
    pending_amount_grand_minus_paid: salePendingGrandMinusPaid(i),
  })) ?? [];

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    setCurrentPage(1);
  }, [documentTypeFilter, user, startdate, enddate, filteredList?.length]);

  const onSwitchChange = (mode) => {
    setViewMode(mode);
    if (mode === "paginated") setCurrentPage(1);
  };


  const isLogged = Boolean(localStorage.getItem("isLogged"));

  if (!isLogged) {
    return <Navigate to={"/auth/login"} replace={true} />;
  }

	const hasActiveFilter = user || documentTypeFilter !== "all";

	const filterContent = (
		<div style={{ width: 280 }}>
			<Form
				onFinish={(values) => {
					onSearchFinish(values);
					setFilterVisible(false);
				}}
				form={form}
				layout="vertical"
				onFinishFailed={() => setLoading(false)}
				initialValues={{
					dateRange: [moment(startdate), moment(enddate)],
					documentType: "all",
				}}
			>
				<Form.Item name='documentType' label="Document Type">
					<Select
						placeholder='Select Document Type'
						style={{ width: '100%' }}
					>
						<Select.Option value='all'>All Documents</Select.Option>
						<Select.Option value='sale'>Sale Only</Select.Option>
						<Select.Option value='quotation'>Quotation Only</Select.Option>
						<Select.Option value='challan'>Challan Only</Select.Option>
						<Select.Option value='sales_order'>Sales Order Only</Select.Option>
					</Select>
				</Form.Item>
				<Form.Item name='user' label="Sale Person">
					<Select
						loading={!userList}
						placeholder='Select Sale Person'
						style={{ width: '100%' }}
						allowClear
					>
						<Select.Option value=''>All</Select.Option>
						{userList &&
							userList.map((i) => (
								<Select.Option key={i.id} value={i.id}>{i.username}</Select.Option>
							))}
					</Select>
				</Form.Item>
				<Form.Item name="dateRange" label="Date Range">
					<RangePicker
						className='range-picker'
						format="DD-MMM-YYYY"
						style={{ width: '100%' }}
					/>
				</Form.Item>
				<Form.Item>
					<Button
						onClick={() => setLoading(true)}
						loading={loading}
						type="primary"
						htmlType="submit"
						block
						icon={<SearchOutlined />}
					>
						Search Sales
					</Button>
				</Form.Item>
			</Form>
		</div>
	);

	return (
		<>
			<div className="card card-custom list-page-fill-viewport sale-list-page">
				<div className="card-body list-page-card-body">
					{/* On mobile this wrapper sticks to top so header + summary stay visible */}
					<div className="list-page-sticky-top">
						<div className="d-flex justify-content-between align-items-center flex-wrap gap-2 list-page-header mb-2" style={{ marginBottom: "8px" }}>
							<h5 className='mb-0'>Sale Invoice List</h5>
				<div className="d-flex flex-wrap align-items-center gap-2">
					<Popover
						content={filterContent}
						title="Filters"
						trigger="click"
						open={filterVisible}
						onOpenChange={setFilterVisible}
						placement="bottomLeft"
					>
						<Badge count={hasActiveFilter ? 1 : 0} size="small" offset={[-2, 2]}>
							<Button
								type="primary"
								icon={<FilterOutlined />}
								onClick={() => setFilterVisible((v) => !v)}
								style={{
									width: 36,
									height: 36,
									borderRadius: '50%',
									padding: 0,
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
								}}
							/>
						</Badge>
					</Popover>
					{list !== null && (
						<>
							<CSVLink
								data={CSVlist}
								className="btn btn-dark btn-sm"
								filename="sales"
							>
								<i className="bi bi-download me-1"></i>
								<span className="d-none d-sm-inline">Download CSV</span>
								<span className="d-sm-none">CSV</span>
							</CSVLink>
							<div className="d-none d-md-block">
								<Segmented
									className="text-center rounded danger"
									size="middle"
									options={[
										{
											label: (
												<span>
													<i className="bi bi-person-lines-fill"></i> All
												</span>
											),
											value: "all",
										},
										{
											label: (
												<span>
													<i className="bi bi-person-dash-fill"></i>{" "}
													Paginated
												</span>
											),
											value: "paginated",
										},
									]}
									value={viewMode}
									onChange={onSwitchChange}
								/>
							</div>
							<div className="d-none d-md-block">
								<SaleReportPrint
									data={listForExport}
									date={{ startdate, enddate }}
									user={user}
									total={total?._sum}
								/>
							</div>
							{columnVisibilityToolbar && (
								<div className="d-none d-md-block">{columnVisibilityToolbar}</div>
							)}
					</>
					)}
				</div>
			</div>
			<DashboardCard
				information={total?._sum}
				count={total?._count}
				isCustomer={true}
			/>
					</div>
			<div className="mt-0 list-page-table-outer">
			<CustomTable
				list={filteredList}
				total={filteredTotal}
				startdate={startdate}
				enddate={enddate}
				user={user}
				onColumnVisibilityReady={setColumnVisibilityToolbar}
				onShowPdf={handleShowPdf}
				onPrintPdf={handlePrintPdf}
				paginationConfig={{
					current: currentPage,
					pageSize,
					totalCount: totalCount,
					viewMode,
					onChange: (page, size) => {
						setCurrentPage(page);
						setPageSize(size || 10);
					},
				}}
			/>
			</div>
		</div>
		</div>

		{/* PDF modal from kebab: Show PDF / Print PDF */}
		{pdfModalOpen && (
			<>
				{pdfModalLoading && (
					<div className="popUp" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
						<Spin size="large" />
					</div>
				)}
				{!pdfModalLoading && pdfModalData && (
					<SaleInvoice
						data={pdfModalData}
						autoShow={true}
						documentType={pdfModalData.document_type || "sale"}
						onClose={() => {
							setPdfModalOpen(false);
							setPdfModalData(null);
						}}
						autoPrintOnce={pdfModalAction === "print"}
					/>
				)}
			</>
		)}
    </>
  );
};

export default GetAllSale;
